import React from 'react';
import api from '../api/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DownloadSimple, Bank, LinkSimple, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : '');

const STATUS = {
  sent: { label: 'Inviato', cls: 'bg-sky-100 text-sky-800' },
  accepted: { label: 'Accettato', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rifiutato', cls: 'bg-red-100 text-red-700' },
  expired: { label: 'Scaduto', cls: 'bg-amber-100 text-amber-800' },
  cancelled: { label: 'Annullato', cls: 'bg-slate-200 text-slate-700' },
};

export const QuoteStatusBadge = ({ status, id }) => {
  const s = STATUS[status] || STATUS.sent;
  return <Badge className={`${s.cls} hover:${s.cls} border-0`} data-testid={`quote-status-${id}`}>{s.label}</Badge>;
};

export const downloadQuotePdf = async (q) => {
  try {
    const r = await api.get(`/api/quotes/${q.quote_id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `preventivo-${q.number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error('Download del PDF non riuscito'); }
};

const PaymentInfo = ({ payment, id }) => {
  if (!payment?.iban && !payment?.link) return null;
  const copy = () => { navigator.clipboard.writeText(payment.iban); toast.success('IBAN copiato'); };
  const label = { stripe: 'Stripe', paypal: 'PayPal' }[payment.link_type] || 'online';
  return (
    <div className="rounded-xl bg-[#F5FAFF] border border-[#E3F2FD] p-3 space-y-2 text-sm" data-testid={`quote-payment-${id}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Modalità di pagamento</div>
      {payment.iban && (
        <div className="flex items-center gap-2 flex-wrap">
          <Bank size={16} className="text-primary" />
          <span className="font-mono" data-testid={`quote-iban-${id}`}>{payment.iban}</span>
          {payment.iban_holder && <span className="text-slate-500">· {payment.iban_holder}</span>}
          <button onClick={copy} className="text-primary hover:underline flex items-center gap-1 text-xs" data-testid={`quote-copy-iban-${id}`}><Copy size={14} />Copia</button>
        </div>
      )}
      {payment.link && (
        <a href={payment.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-primary hover:underline" data-testid={`quote-pay-link-${id}`}>
          <LinkSimple size={16} /> Paga con {label}
        </a>
      )}
    </div>
  );
};

const QuoteDetails = ({ quote: q }) => (
  <div className="space-y-3" data-testid={`quote-details-${q.quote_id}`}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-2">Voce</th><th className="text-right">Q.tà</th><th className="text-right">Prezzo</th><th className="text-right">Importo</th></tr></thead>
        <tbody>
          {q.items.map((i, idx) => (
            <tr key={idx} className="border-b border-slate-100">
              <td className="py-2">{i.description}</td><td className="text-right">{i.quantity}</td>
              <td className="text-right">{eur(i.unit_price)}</td><td className="text-right">{eur(i.quantity * i.unit_price)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="text-sm text-right space-y-0.5">
      <div>Imponibile <span className="font-medium">{eur(q.subtotal)}</span></div>
      {q.vat_enabled && <div>IVA {q.vat_rate}% <span className="font-medium">{eur(q.vat_amount)}</span></div>}
      <div className="text-base font-heading font-semibold text-primary" data-testid={`quote-total-${q.quote_id}`}>Totale {eur(q.total)}</div>
    </div>
    {q.notes && <p className="text-sm text-slate-600 whitespace-pre-line" data-testid={`quote-notes-${q.quote_id}`}>{q.notes}</p>}
    {q.reject_reason && <p className="text-sm text-red-700">Motivo del rifiuto: {q.reject_reason}</p>}
    <PaymentInfo payment={q.payment} id={q.quote_id} />
    <Button variant="outline" size="sm" onClick={() => downloadQuotePdf(q)} data-testid={`quote-pdf-${q.quote_id}`}>
      <DownloadSimple size={16} className="mr-1" /> Scarica PDF
    </Button>
  </div>
);

export default QuoteDetails;
